/* eslint-disable @typescript-eslint/no-explicit-any */
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from 'sonner'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useManualPlanMutation } from '@/hooks/usePlatformOwnerDetail'

const schema = z.object({
  plan: z.enum(['MONTHLY_STANDARD', 'YEARLY_ECO', 'LIFETIME_PREMIUM']),
  paidAt: z.string().min(1, 'paidAt requis'), // ISO input
  amountCfa: z.number().int().positive('Montant invalide'),
  note: z.string().optional().or(z.literal('')),
  mode: z.enum(['EXTEND', 'RESET']),
})
type Values = z.infer<typeof schema>

export function ManualPlanCard({
  ownerId,
  currentPlan,
}: {
  ownerId: string
  currentPlan: 'MONTHLY_STANDARD' | 'YEARLY_ECO' | 'LIFETIME_PREMIUM'
}) {
  const mut = useManualPlanMutation(ownerId)

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      plan: currentPlan,
      paidAt: new Date().toISOString(),
      amountCfa:
        currentPlan === 'YEARLY_ECO'
          ? 300_000
          : currentPlan === 'LIFETIME_PREMIUM'
          ? 600_000
          : 30_000,
      note: '',
      mode: 'EXTEND',
    },
  })

  const submit = async (v: Values) => {
    try {
      // validation date
      const d = new Date(v.paidAt)
      if (Number.isNaN(d.getTime())) {
        toast.error('paidAt invalide')
        return
      }

      const res = await mut.mutateAsync({
        plan: v.plan,
        paidAt: d.toISOString(),
        amountCfa: v.amountCfa,
        note: v.note?.trim() || undefined,
        mode: v.mode ?? 'EXTEND',
      })
      toast.success('Plan appliqué', {
        description: `paymentId: ${res.paymentId}`,
      })
    } catch (e: any) {
      toast.error('Action impossible', {
        description: e?.response?.data?.message ?? 'Erreur inconnue',
      })
    }
  }

  return (
    <Card>
      <CardHeader>
        <h3 className='text-lg font-semibold'>Manual plan payment</h3>
        <p className='text-sm text-muted-foreground'>
          Mode par défaut: <b>EXTEND</b> (prolonge si payé avant expiration).
        </p>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(submit)} className='space-y-4'>
            <FormField
              control={form.control}
              name='plan'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Plan</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value='MONTHLY_STANDARD'>
                        MONTHLY_STANDARD
                      </SelectItem>
                      <SelectItem value='YEARLY_ECO'>YEARLY_ECO</SelectItem>
                      <SelectItem value='LIFETIME_PREMIUM'>
                        LIFETIME_PREMIUM
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='mode'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Mode</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value='EXTEND'>EXTEND</SelectItem>
                      <SelectItem value='RESET'>RESET</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='paidAt'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Paid at (ISO)</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder='2026-02-04T12:00:00.000Z' />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='amountCfa'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount (CFA)</FormLabel>
                  <FormControl>
                    <Input
                      type='number'
                      name={field.name}
                      value={field.value}
                      onBlur={field.onBlur}
                      ref={field.ref}
                      onChange={(e) => field.onChange(e.target.valueAsNumber)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='note'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Note</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder='Wave dépôt / cash / etc.' />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className='flex justify-end'>
              <Button type='submit' disabled={mut.isPending}>
                {mut.isPending ? 'Application…' : 'Appliquer'}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}
